/* eslint-disable @typescript-eslint/no-explicit-any */
import * as yup from 'yup'
import dayjs from 'dayjs'

export type SegmentationValues = {
  targetOptions: string[] | string
  memberTiers?: { memberId: number; memberName: string }[] | null
  CarModelYears?: { CarModelId: number; CarModelName?: string; ModelYear: number[] }[]
  salutation?: string
  locations?: {
    provinceId: number | null
    provinceName?: string
    cityId: number[]
    cities: string[]
  }[]
  carOwnership?: { id: string; name: string }[]
  licensePlateType?: string
  activeStatus?: string
  bluelinkStatus?: string
  vinCsvFile?: File | null
  vinCsvFileUrl?: string | null
  memberIdCsvFile?: File | null
  memberIdCsvFileUrl?: string | null
  handoverDateRange?: { minDate: string; maxDate: string }
}

type MemberTierSelection = {
  memberName: string
}

const COMBINABLE_MEMBER_TIERS = ['blue', 'blue+', 'signature']

const normaliseMemberTierName = (memberName: string) => memberName.trim().toLowerCase()

export const isValidMemberTierSelection = (memberTiers: MemberTierSelection[]) =>
  memberTiers.length <= 1 ||
  memberTiers.every((memberTier) =>
    COMBINABLE_MEMBER_TIERS.includes(normaliseMemberTierName(memberTier.memberName))
  )

export const MEMBER_TIER_COMBINATION_ERROR =
  'Only Blue, Blue+, and Signature can be combined. White must be selected alone.'

export const SEGMENTATION_DELETE_FIELDS: string[] = [
  'CarModelYears',
  'salutation',
  'vinCsvFile',
  'memberIdCsvFile',
  'vinCsvFileUrl',
  'memberIdCsvFileUrl',
  'locations',
  'carOwnership',
  'licensePlateType',
  'activeStatus',
  'bluelinkStatus',
  'memberTiers',
  'handoverDateRange'
]

export const COMBINABLE_SEGMENTATION_OPTIONS = [
  'Location',
  'Salutation',
  'Active Status',
  'Handover Date'
]

export function buildSegmentationJson(values: SegmentationValues): string {
  const {
    targetOptions,
    memberTiers,
    CarModelYears,
    salutation,
    locations,
    licensePlateType,
    activeStatus,
    bluelinkStatus,
    handoverDateRange
  } = values
  const segmentation: Record<string, any> = {}

  for (const option of targetOptions) {
    const opt = option.toLowerCase()
    if (opt === 'vin') {
      segmentation.vinCsvFileUrl = ''
    } else if (opt === 'upload member id') {
      segmentation.memberIdCsvFileUrl = ''
    } else if (opt === 'member tier target') {
      segmentation.memberTiers = memberTiers
    } else if (opt === 'car model target') {
      segmentation.CarModelYears = CarModelYears
    } else if (opt === 'salutation') {
      segmentation.salutation = salutation
    } else if (opt === 'location') {
      segmentation.locations = locations
    } else if (opt === 'car ownership') {
      segmentation.carOwnership = values.carOwnership
    } else if (opt === 'license plate type') {
      segmentation.licensePlateType = licensePlateType
    } else if (opt === 'active status') {
      segmentation.activeStatus = activeStatus
    } else if (opt === 'bluelink status') {
      segmentation.bluelinkStatus = bluelinkStatus
    } else if (opt === 'handover date') {
      segmentation.handoverDateRange = handoverDateRange
    }
  }

  if (Object.keys(segmentation).length === 0) {
    return ''
  }

  return JSON.stringify(segmentation)
}

export const targetOptionsSchema = yup
  .array()
  .of(yup.string())
  .optional()
  .test(
    'combinable-options',
    'Only Location, Salutation, Active Status, and Handover Date can be combined. Other options must be selected alone.',
    (val: (string | undefined)[] | undefined) => {
      if (!val || val.length <= 1) return true
      return val.every((v: string | undefined) =>
        COMBINABLE_SEGMENTATION_OPTIONS.some((opt) => opt.toLowerCase() === v?.toLowerCase())
      )
    }
  )
  .test(
    'max-two-combinations',
    'You can only combine up to 2 target options at a time.',
    (val: (string | undefined)[] | undefined) => !val || val.length <= 2
  )

const memberTiersSchema = (restrictCombinations = false) =>
  yup.array().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'member tier target'),
    then: (schema) => {
      const requiredSchema = schema.min(1, 'At least one of Member Tier Target must be selected')

      return restrictCombinations
        ? requiredSchema.test(
            'member-tier-combination',
            MEMBER_TIER_COMBINATION_ERROR,
            (memberTiers) => !memberTiers || isValidMemberTierSelection(memberTiers)
          )
        : requiredSchema
    }
  })

export const segmentationSchemaShape = {
  targetOptions: targetOptionsSchema,
  memberTiers: memberTiersSchema(),
  vinCsvFile: yup.mixed().when('targetOptions', {
    is: (val: string[]) => Array.isArray(val) && val.some((v) => v.toLowerCase() === 'vin'),
    then: (schema) => schema.required('A CSV file is required for VIN target.'),
    otherwise: (schema) => schema.notRequired()
  }),
  memberIdCsvFile: yup.mixed().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'upload member id'),
    then: (schema) => schema.required('A CSV file is required for Upload Member Id target.'),
    otherwise: (schema) => schema.notRequired()
  }),
  CarModelYears: yup.array().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'car model target'),
    then: (schema) =>
      schema.of(
        yup.object({
          CarModelId: yup.number().required('Car Model is required.'),
          ModelYear: yup
            .array()
            .of(yup.number())
            .max(5, 'You can put up to 5 selection of Car Model Year')
        })
      ),
    otherwise: (schema) => schema.notRequired()
  }),
  salutation: yup.string().when('targetOptions', {
    is: (val: string[]) => Array.isArray(val) && val.some((v) => v.toLowerCase() === 'salutation'),
    then: (schema) => schema.required('Salutation is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  locations: yup.array().when('targetOptions', {
    is: (val: string[]) => Array.isArray(val) && val.some((v) => v.toLowerCase() === 'location'),
    then: (schema) =>
      schema
        .of(
          yup.object({
            provinceId: yup.number().nullable(),
            provinceName: yup.string(),
            cityId: yup.array().of(yup.number()).min(1, 'At least one of city must be selected'),
            cities: yup.array().of(yup.string())
          })
        )
        .min(1, 'At least one location must be selected'),
    otherwise: (schema) => schema.notRequired()
  }),
  carOwnership: yup.array().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'car ownership'),
    then: (schema) => schema.min(1, 'At least one of vehicle type ownership must be selected')
  }),
  licensePlateType: yup.string().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'license plate type'),
    then: (schema) => schema.required('License Plate Type is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  activeStatus: yup.string().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'active status'),
    then: (schema) => schema.required('Active Status is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  bluelinkStatus: yup.string().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'bluelink status'),
    then: (schema) => schema.required('Bluelink Status is required.'),
    otherwise: (schema) => schema.notRequired()
  }),
  handoverDateRange: yup.object().when('targetOptions', {
    is: (val: string[]) =>
      Array.isArray(val) && val.some((v) => v.toLowerCase() === 'handover date'),
    then: (schema) =>
      schema.shape({
        minDate: yup
          .string()
          .required('Min Handover Date is required.')
          .test(
            'minDate-before-maxDate',
            'Min Handover Date must be earlier than Max Handover Date',
            function (minDateValue) {
              const { maxDate } = this.parent
              if (!minDateValue || !maxDate) return true
              return dayjs(minDateValue).isBefore(dayjs(maxDate))
            }
          ),
        maxDate: yup
          .string()
          .required('Max Handover Date is required.')
          .test(
            'maxDate-after-minDate',
            'Max Handover Date must be later than Min Handover Date',
            function (maxDateValue) {
              const { minDate } = this.parent
              if (!maxDateValue || !minDate) return true
              return dayjs(maxDateValue).isAfter(dayjs(minDate))
            }
          )
          .test(
            'maxHandoverDate-before-airingTime',
            'Max Handover Date must be earlier than Airing Time',
            function (maxDateValue) {
              const { airingTime } = this.options.context as { airingTime?: string }
              if (!maxDateValue || !airingTime) {
                return true
              }
              return dayjs(maxDateValue).isBefore(dayjs(airingTime))
            }
          )
      }),
    otherwise: (schema) => schema.nullable().notRequired()
  })
}

export const pushNotificationSegmentationSchemaShape = {
  ...segmentationSchemaShape,
  memberTiers: memberTiersSchema(true)
}
